import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Tab, Tabs, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { useAccountDetails } from './hooks/useAccountDetails';
import { useBreadcrumbLabel } from '../../../../../shared/hooks/useBreadcrumbLabel';
import { getStatusVariant } from '../../../domain/models/AccountStatusUtils';
import { getAccountStatusLabel } from '../../../domain/models/AccountStatus';
import { StatusBadge } from '../../../../../shared/components/ui/StatusBadge';
import { AccountDetailsSummaryTab } from './components/AccountDetailsSummaryTab';
import { AccountDetailsAccountTab } from './components/AccountDetailsAccountTab';
import { AccountDetailsBillingTab } from './components/AccountDetailsBillingTab';
import { AccountDetailsIntegrationsTab } from './components/AccountDetailsIntegrationsTab';
import { AccountDetailsSupportTab } from './components/AccountDetailsSupportTab';
import { AccountDangerZone } from './components/AccountDangerZone';
import { useServices } from '../../../../../core/contexts/useServices';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`account-tabpanel-${index}`}
    aria-labelledby={`account-tab-${index}`}
  >
    {value === index && <Box>{children}</Box>}
  </div>
);

const tabA11yProps = (index: number) => ({
  id: `account-tab-${index}`,
  'aria-controls': `account-tabpanel-${index}`,
});

/**
 * AccountDetailsPage
 * Displays comprehensive account information across 5 tabs.
 */
export const AccountDetailsPage: React.FC = () => {
  const { accountId } = useParams<{ accountId: string }>();
  const navigate = useNavigate();
  const { account, details, loading, error, retry } = useAccountDetails(accountId);
  const { accountService } = useServices();
  const [activeTab, setActiveTab] = useState(0);
  useBreadcrumbLabel(accountId, account?.name);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || !account || !details) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error ?? 'Account not found'}
        </Alert>
        <Button variant="contained" onClick={retry}>
          Retry
        </Button>
      </Box>
    );
  }

  const handleEditClick = () => {
    navigate(`/accounts/${accountId}/edit`);
  };

  const handleDeleteAccount = async () => {
    if (!accountId) return;
    await accountService.deleteAccount(accountId);
    navigate('/accounts');
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography
            component="h1"
            sx={{
              fontSize: '24px',
              fontWeight: 600,
              lineHeight: '32px',
              letterSpacing: '-0.01em',
              color: '#0b1c30',
            }}
          >
            {account.name}
          </Typography>
          <StatusBadge
            label={getAccountStatusLabel(account.status)}
            status={getStatusVariant(account.status)}
          />
        </Box>
        <Button
          variant="contained"
          startIcon={<EditIcon />}
          onClick={handleEditClick}
          sx={{ bgcolor: '#003ec7', '&:hover': { bgcolor: '#0031a0' }, boxShadow: 'none' }}
        >
          Edit
        </Button>
      </Box>

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onChange={(_, newValue: number) => setActiveTab(newValue)}
        aria-label="Account details tabs"
        sx={{ borderBottom: '1px solid #E2E8F0', mb: 0 }}
      >
        <Tab label="Summary" {...tabA11yProps(0)} />
        <Tab label="Account" {...tabA11yProps(1)} />
        <Tab label="Billing" {...tabA11yProps(2)} />
        <Tab label="Integrations" {...tabA11yProps(3)} />
        <Tab label="Support" {...tabA11yProps(4)} />
      </Tabs>

      <TabPanel value={activeTab} index={0}>
        <AccountDetailsSummaryTab account={account} details={details} />
      </TabPanel>
      <TabPanel value={activeTab} index={1}>
        <AccountDetailsAccountTab account={account} />
      </TabPanel>
      <TabPanel value={activeTab} index={2}>
        <AccountDetailsBillingTab details={details} />
      </TabPanel>
      <TabPanel value={activeTab} index={3}>
        <AccountDetailsIntegrationsTab details={details} />
      </TabPanel>
      <TabPanel value={activeTab} index={4}>
        <AccountDetailsSupportTab details={details} />
      </TabPanel>

      {account.status === 'removed' && (
        <AccountDangerZone accountName={account.name} onDelete={handleDeleteAccount} />
      )}
    </Box>
  );
};
